import { useState, useEffect, createContext, useContext, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { isExpiredSessionErrorResponse, isSessionTokenError } from '../services/responseUtils';
import { getUserSessionContextGracefully } from '../veevaIdApi';
import InvalidSessionDialog from './InvalidSessionDialog';
import SessionExpiredDialog from './SessionExpiredDialog';

const UserSessionContext = createContext({
    handleResponseSessionErrors: (response) => {},
    enableVeevaIdUsernameUpdate: false,
});

export const useUserSessionContext = () =>
    useContext(UserSessionContext) ?? {
        handleResponseSessionErrors: () => {},
        enableVeevaIdUsernameUpdate: false,
    };

const UserSessionContextWrapper = ({ children }) => {
    const history = useHistory();
    const [enableVeevaIdUsernameUpdate, setEnableVeevaIdUsernameUpdate] = useState(false);
    const [validSession, setValidSession] = useState(false);
    const [showInvalidSessionDialog, setShowInvalidSessionDialog] = useState(false);
    const [showSessionExpiredDialog, setShowSessionExpiredDialog] = useState(false);

    useEffect(() => {
        (async () => {
            const responseData = await getUserSessionContextGracefully();

            setEnableVeevaIdUsernameUpdate(responseData?.feature?.enableVeevaIdUsernameUpdate);

            if (responseData && responseData.vid_ww) {
                setValidSession(true);
                sessionStorage.setItem('vid_ww', responseData.vid_ww);
                return;
            }

            history.replace(`/`);
        })();
    }, [history]);

    const handleResponseSessionErrors = useCallback((response) => {
        if (isSessionTokenError(response)) {
            setShowInvalidSessionDialog(true);
            return true;
        }
        if (isExpiredSessionErrorResponse(response)) {
            setShowSessionExpiredDialog(true);
            return true;
        }

        return false;
    }, []);

    const contextValue = useMemo(
        () => ({
            handleResponseSessionErrors,
            enableVeevaIdUsernameUpdate,
        }),
        [handleResponseSessionErrors, enableVeevaIdUsernameUpdate],
    );

    if (!validSession) {
        return null;
    }

    return (
        <UserSessionContext.Provider value={contextValue}>
            {children}
            {showInvalidSessionDialog ? <InvalidSessionDialog /> : null}
            {showSessionExpiredDialog ? <SessionExpiredDialog /> : null}
        </UserSessionContext.Provider>
    );
};
UserSessionContextWrapper.propTypes = {
    children: PropTypes.node,
};

export default UserSessionContextWrapper;
